import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { AMBIENT_SCRIBE_CUSTOM_TEMPLATES } from "constants/feature-flags";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import useTheme from "@mui/material/styles/useTheme";
import AmbientScribeTemplateSelect, {
  AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS
} from "features/medical-scribe/components/ambient-scribe-template-select.component";
import {
  useGenerateAmbientScribeAsyncMutation,
  useGetAmbientScribeTemplatesQuery
} from "services/ambient-scribe";

type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

type GenerateScribeSectionProps = {
  scribeId: number;
  isGeneratingSoap: boolean | null | undefined;
  scribeTemplateId?: number | null;
  onGenerateStarted: () => void;
};

export default function GenerateScribeSection({
  scribeId,
  isGeneratingSoap,
  scribeTemplateId,
  onGenerateStarted
}: Readonly<GenerateScribeSectionProps>) {
  const theme = useTheme();
  const flags = useFlags();
  const isAmbientScribeCustomTemplatesEnabled =
    flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES];
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );

  const [generateError, setGenerateError] = useState<GenerateError | null>(
    null
  );

  const { data: templates = [] } = useGetAmbientScribeTemplatesQuery(
    AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS,
    { refetchOnMountOrArgChange: true }
  );
  const [generateAsync, { isLoading: isGenerateInProgress }] =
    useGenerateAmbientScribeAsyncMutation();

  const preselectedTemplateId = useMemo(() => {
    if (
      scribeTemplateId == null ||
      !templates.some((t) => t.id === scribeTemplateId)
    ) {
      return null;
    }
    return scribeTemplateId;
  }, [templates, scribeTemplateId]);

  const templateId = useMemo(() => {
    const raw =
      selectedTemplateId != null ? selectedTemplateId : preselectedTemplateId;
    if (raw == null || !templates.some((t) => t.id === raw)) {
      return null;
    }
    return raw;
  }, [selectedTemplateId, preselectedTemplateId, templates]);

  const hasTemplates = templates.length > 0;
  const isButtonLoading = Boolean(isGeneratingSoap) || isGenerateInProgress;

  const handleGenerate = async () => {
    const payload: { scribe_id: number; ambient_scribe_template_id?: number } =
      {
        scribe_id: scribeId
      };
    if (
      isAmbientScribeCustomTemplatesEnabled &&
      hasTemplates &&
      templateId != null
    ) {
      payload.ambient_scribe_template_id = templateId;
    }
    const result = await generateAsync(payload);
    if ("error" in result) {
      const err = result.error as any;
      if (err?.status === 402 && err?.data?.subscription_state) {
        setGenerateError({
          kind: "subscription",
          error: err.data.error,
          message: err.data.message
        });
      } else {
        setGenerateError({ kind: "generic" });
      }
      return;
    }
    onGenerateStarted();
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <AmbientScribeTemplateSelect
          value={templateId}
          onChange={setSelectedTemplateId}
          disabled={isButtonLoading}
          templates={templates}
        />

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          loading={isButtonLoading}
          disabled={
            isButtonLoading ||
            (templateId == null &&
              isAmbientScribeCustomTemplatesEnabled &&
              hasTemplates)
          }
          size="small"
        >
          {isAmbientScribeCustomTemplatesEnabled && hasTemplates
            ? "Generate"
            : "Generate SOAP Notes"}
        </LoadingButton>
      </Stack>

      <MuiDialog
        open={generateError !== null}
        onClose={() => setGenerateError(null)}
        sx={{ zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) }}
      >
        <DialogTitle>
          {generateError?.kind === "subscription"
            ? "Subscription Required"
            : "Generation Failed"}
        </DialogTitle>
        <DialogContent>
          {generateError?.kind === "subscription" ? (
            <Alert severity="warning">
              <AlertTitle>{generateError.error}</AlertTitle>
              {generateError.message}
            </Alert>
          ) : (
            <Typography>
              Something went wrong while processing your request. Please try
              again in a few moments. If the issue persists, contact Suno
              support for assistance.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateError(null)}>Close</Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
